import {
  BlogPostFragment,
  MediaWithCropsFragment,
} from '@lib/umbraco/generated/types.generated'
import { isMobile, isTablet } from '@utils/helpers/DeviceUtils'
import cn from 'clsx'
import { FC, useEffect, useState } from 'react'
import { ModuleProps } from '..'

import { UmbracoLink } from '@customTypes/umbracoLink'
import clsx from 'clsx'
import { useQueryState } from 'next-usequerystate'
import { useRouter } from 'next/router'
import BlogPostSlider from '../BlogPostSlider/BlogPostSlider'
import StoriesPost from '../StoriesPost/StoriesPost'
import styles from './StoriesPostGrid.module.scss'

type StoriesPostGridProps = {
  blogPosts?: {
    storiesTeaser?: string
    image?: MediaWithCropsFragment
    storiesTitle?: string
    linkTo?: UmbracoLink
    itemWidth?: number
    sliderItem?: boolean
    priority?: boolean
    category?: string[]
  }[]
  columnCount?: number
  maxWidth?: number
  enableDesktopSlider?: boolean
  enableMobileSlider?: boolean
  controls?: boolean
  disableCategories?: boolean
} & ModuleProps

const StoriesPostGrid: FC<StoriesPostGridProps> = ({
  blogPosts,
  maxWidth,
  columnCount: columns = 4,
  enableDesktopSlider,
  enableMobileSlider,
  controls,
  disableCategories,
  lcp,
}) => {
  const [slider, setSlider] = useState(false)
  // const [spgWidth, setSpgWidth] = useState(0)

  // useEffect(() => {}, [])
  useEffect(() => {
    //resize observer to check if the slider should be enabled
    const resizeObserver = new ResizeObserver(() => {
      setSlider(
        (enableMobileSlider && isMobile() && !isTablet()) ||
          (enableDesktopSlider && !isMobile()),
      )

      // setSpgWidth(document.querySelector(`.${styles.container}`)?.clientWidth)
    })
    resizeObserver.observe(document.body)
    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  // useEffect(() => {
  //   document.body.style.setProperty('--spg-width', spgWidth + 'px')
  // }, [spgWidth])

  const categories = [
    ...new Set(
      blogPosts
        ?.map((post) => post.category)
        .flat()
        .map((x) => x.toLowerCase()),
    ),
  ]

  // order categories by number of posts in each category
  categories.sort((a, b) => {
    const aCount = blogPosts?.filter((post) =>
      post.category?.includes(a),
    ).length
    const bCount = blogPosts?.filter((post) =>
      post.category?.includes(b),
    ).length
    return bCount - aCount
  })

  // handle category selection
  const router = useRouter()
  const [categoryFilter, _] = useQueryState('category')

  const setSelectedCategory = (category: string) => {
    // if category is already selected, remove filter
    if (categoryFilter === category) {
      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            category: null,
          },
        },
        undefined,
        { scroll: false },
      )
    } else {
      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            category,
          },
        },
        undefined,
        { scroll: false },
      )
    }
  }

  useEffect(() => {
    if (disableCategories) return

    const active = document.querySelector(`.${styles.active}`)
    if (active) active.classList.remove(styles.active)
    const newActive = document.querySelector(
      `[data-category="${categoryFilter?.length > 0 ? categoryFilter : 'all'}"]`,
    )
    if (newActive) newActive.classList.add(styles.active)
  }, [categoryFilter, disableCategories])

  return (
    <>
      {blogPosts && slider ? (
        <BlogPostSlider
          blogPosts={
            blogPosts.map(
              (blogPost) =>
                ({
                  ...blogPost,
                  title: blogPost.storiesTitle,
                  bodyText: blogPost.storiesTeaser,
                }) as BlogPostFragment,
            ) as BlogPostFragment[]
          }
          controls={controls}
        />
      ) : (
        <>
          <div className={styles.categoryContainer}>
            <ul className={styles.categoryWrapper}>
              {categories.length > 0 && (
                <li
                  className={clsx({
                    [styles.active]: router.query.category?.length === 0,
                  })}
                  data-category="all"
                  onClick={() => setSelectedCategory(null)}
                >
                  <span className={styles.underlineAnimation}>All</span>
                </li>
              )}
              {categories
                .sort((a, b) => a.localeCompare(b))
                .map((category) => (
                  <li
                    className={clsx({
                      [styles.active]: router.query.category === category,
                    })}
                    key={category}
                    data-category={category}
                    onClick={() => setSelectedCategory(category)}
                  >
                    <span className={styles.underlineAnimation}>
                      {category}
                    </span>
                  </li>
                ))}
            </ul>
            <div className={styles.scrollMore} />
          </div>
          <div
            style={{
              maxWidth: maxWidth ? `${maxWidth}vw` : 'unset',
            }}
            className={cn(styles.container)}
          >
            {blogPosts &&
              blogPosts
                .filter((post) =>
                  categoryFilter || router.query.category
                    ? post.category
                        .map((x) => x.toLowerCase())
                        .includes(
                          categoryFilter || (router.query.category as string),
                        )
                    : true,
                )
                .map((blogPost, index) => (
                  <StoriesPost
                    itemWidth={columns ? 1 / columns : 1 / 3}
                    sliderItem={false}
                    key={index}
                    {...blogPost}
                  />
                ))}
          </div>
        </>
      )}
    </>
  )
}

export default StoriesPostGrid

export const generateSlides = (
  size: number,
  slides: HTMLElement[],
  perView: number,
  spacing: number,
) => {
  spacing = spacing / size || 0
  const spacingPortion = spacing / perView

  let checkedLength = 0
  let length =
    (1 / perView - spacing + spacingPortion) * slides.length +
    spacing * (slides.length - 1)

  return slides.map((_, index) => {
    const slideSize = 1 / perView - spacing + spacingPortion
    const space = length - checkedLength
    checkedLength += slideSize + spacing
    if (index === 0)
      return {
        size: slideSize,
        spacing: spacing,
        origin: 0,
      }

    if (space >= 1)
      return {
        size: slideSize,
        spacing: spacing,
        origin: 0,
      }
    if (index === slides.length - 1)
      return {
        size: slideSize,
        spacing: spacing,
        origin: 1 - space - spacing - 0.06,
      }
    return {
      size: slideSize,
      spacing: spacing,
      origin: 1 - space - (length > 1 ? 0 : 1 - length),
    }
  })
}
